// @flow
import _ from 'lodash';
import { checkStatus } from './helpers';

// This module contains a factory for the oauth module.
// TODO: pull out into a separate dependency that can be used in our non-store clients

const GET_TOKEN_PATH = 'auth/token';
const CREATE_TOKEN_PATH = 'auth/create';

export type UserCredentials = {username?: string, password?: string};
export type TokenData = {token_type: string, access_token: string}
export type CredentialStorage = {
  getUserCredentials: () => Promise<?UserCredentials>,
  setUserCredentials: (credentials: UserCredentials) => Promise<*>,
  resetUserCredentials: () => Promise<*>,
  getToken: () => Promise<?TokenData>,
  setToken: (token_data: TokenData) => Promise<*>,
  resetToken: () => Promise<*>,
  resetTokenAndUserCredentials: () => Promise<*>
}

export type ClientCredentials = {client_id: string, client_secret: string};
export type GetClientCredentials = () => ClientCredentials;
export type GetBaseUrl = () => string;

const buildOAuth = (credential_storage: CredentialStorage, getClientCredentials: GetClientCredentials, getBaseUrl: GetBaseUrl) => {
  // This should be called both on application startup (resume and cold start), when
  // there is a change in login state, and if a normal API request recieves a 401 error.
  const getToken = () => {
    // first, we attempt to grab the stored oauth credentials
    const prom = credential_storage.getToken()
      .then(token => {
        if (token){ // return the token if we have it stored
          return Promise.resolve(token);
        } else { // otherwise, request it and then re-attempt to pull it out of storage
          return fetchTokenWithStoredCredentials().then(() => credential_storage.getToken());
        }
      });

    return prom;
  };

  // As of Jan 2017, a Minibar OAuth token has a lifetime of 72 hours.
  const fetchTokenWithStoredCredentials = (account_info: Object = {}) => {
    const prom = credential_storage.getUserCredentials()
      .then(credentials => credentials || {})
      // we pass the credentials (or lack thereof) on to our token fetching logic
      // if account info present without credentials we throw before the fetch
      .then(credentials => { // FIXME: this likely doesn't belong in here
        if (someValues(account_info) && !someValues(credentials)){
          return handleInvalidCreate();
        } else {
          return fetchToken(credentials, account_info);
        }
      });

    return prom;
  };

  // If we have user credentials then we will attempt to request a resource owner
  // credentials grant, associating the access_token with a specific user account.
  // This negates the need for a `X-Minibar-User-Token` header.
  //
  // This approach relies on the stored internet credentials stored to be
  // in sync with the state of the app. If a user logs or changes their password
  // then they should be set. If a user signs out then they should be reset.

  const fetchToken = (credentials: ?UserCredentials = {}, account_info: ?Object = {}) => {
    const { client_id, client_secret } = getClientCredentials();
    const url = `${getBaseUrl()}/${someValues(account_info) ? CREATE_TOKEN_PATH : GET_TOKEN_PATH}`;

    const errorHandler = () => {
      if (!someValues({...credentials, ...account_info})){ // if no credentials given handle as client credential
        return handleOauthClientCredentialsError;
      } else {
        if (someValues(account_info) && someValues(credentials)){ // if credentials and account_info handle as create resource owner
          return handleOauthCreateResourceOwnerCredentialsError;
        }
        // case of someValues(account_info) && !someValues(credentials) covered by handleInvalidCreate
        // otherwise treat as resource owner credential fetch
        return handleOauthResourceOwnerCredentialsError;
      }
    };

    const prom = fetch(url, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        client_id,
        client_secret,
        grant_type: 'password',
        email: credentials && credentials.username,
        password: credentials && credentials.password,
        password_confirmation: credentials && credentials.password,
        ...account_info
      })
    }).then(checkStatus)
      .then(response => response.json())
      .then(handleOAuthSuccess, errorHandler());

    return prom;
  };

  const handleOAuthSuccess = (response) => {
    const returnResponse = () => response;

    return credential_storage
      .setToken({token_type: response.token_type, access_token: response.access_token})
      .then(returnResponse);
  };

  const handleOauthClientCredentialsError = (error) => {
    // This will happen if the network request times out or the SERVER returns an
    // error response. In this case we may want to consider:
    //
    // 1. Retrying the request in the case of time out or 5xx response code. We should have
    //    an incremental back-off strategy, finally giving up and showing a generic network error & retry dialog.
    //
    // 2. If 401 response for client_credentials grant then either invalid request format
    //    (unlikely) or app's access token has been revoked (also unlikely).
    //

    console.info('Client credential grant failed.', error);
    return Promise.reject(error);
  };

  const handleOauthResourceOwnerCredentialsError = (error) => {
    // This will happen if we attempt a resource owner grant and get a 401 response,
    // usually this means that the user credentials we have are out of sync. Perhaps the
    // user has changed them somewhere else. We should reset the credentials and try
    // a basic client_credential grant.
    //
    // Do we consider resetting them and having user authenticate or an error/retry dialog?
    //
    // TODO: How is this handled in the rest of the application? What do we need to do
    // in order to log the user out?
    console.info('Resource owner credential grant failed. Perhaps credentials are invalid.', error);
    return credential_storage.resetUserCredentials().then(() => fetchTokenWithStoredCredentials());
  };

  const handleOauthCreateResourceOwnerCredentialsError = (error) => {
    // This will occur if we received an error back when attempting to create a user
    // and unlike handleOauthResourceOwnerCredentialsError, we will not attempt to setToken

    console.info('Resource owner create credential grant failed. Perhaps credentials already exist.', error);
    return Promise.reject(error);
  };

  const handleInvalidCreate = () => {
    // Should only happen in the case that something is attempting to create a new
    // user in the presence of accoun_info, but lacking UserCredentials
    const error_message = 'No username and password found. Cannot create new user.';
    console.info(error_message);
    return Promise.reject(error_message);
  };

  return {
    getToken,
    fetchTokenWithStoredCredentials,
    fetchToken
  };
};

export default buildOAuth;

// helpers

const someValues = (obj: Object) => _.some(_.filter(obj));
